import { registrarError } from '../../domain/errores';
import { aNumero, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';

/** Página de resultados con el total, para poder pintar la paginación. */
export interface Pagina<T> {
  filas: T[];
  total: number;
}

/** Rango de filas que hay que pedir al servidor para una página dada. */
export function rangoDePagina(pagina: number, porPagina: number): { desde: number; hasta: number } {
  const desde = Math.max(0, (pagina - 1) * porPagina);
  return { desde, hasta: desde + porPagina - 1 };
}

/**
 * Construye un diccionario de identificador a nombre.
 *
 * Varias pantallas muestran filas que sólo guardan el identificador de otra
 * cosa —el cliente de un medidor, el actuador de una regla— y necesitan su
 * nombre. Resolverlo con una consulta por lotes evita tanto repetir este
 * código en cada repositorio como pedir el nombre uno por uno.
 *
 * Si la consulta falla se devuelve un diccionario vacío en vez de propagar el
 * error: un nombre que falta degrada la pantalla, pero no impide usarla.
 */
export async function leerNombres<T extends string | number>(
  tabla: string,
  ids: readonly T[],
  opciones: {
    /** Columna que identifica la fila. */
    columnaId?: string;
    /** Columnas de las que sacar el nombre, en orden de preferencia. */
    columnasNombre?: readonly string[];
  } = {},
): Promise<Map<T, string>> {
  const mapa = new Map<T, string>();
  if (ids.length === 0) return mapa;

  const columnaId = opciones.columnaId ?? 'id';
  const columnasNombre = opciones.columnasNombre ?? ['nombre'];

  const { data, error } = await supabase
    .from(tabla)
    .select([columnaId, ...columnasNombre].join(', '))
    .in(columnaId, [...new Set(ids)]);

  if (error) {
    registrarError(`consultas.leerNombres.${tabla}`, error);
    return mapa;
  }

  for (const fila of (data ?? []) as unknown as Record<string, unknown>[]) {
    const bruto = fila[columnaId];
    const id = (typeof ids[0] === 'number' ? aNumero(bruto) : aTexto(bruto)) as T | null;
    if (id === null) continue;

    for (const columna of columnasNombre) {
      const nombre = aTexto(fila[columna]);
      if (nombre) {
        mapa.set(id, nombre);
        break;
      }
    }
  }

  return mapa;
}
